import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { Filter, PageInfo } from '../models/dynamic-filter.model';
import { SHARED_LIB_CONFIG, SharedLibConfig } from '../config/lib-config';
import { ResponsePackage } from '../models/util.model';
import { DynamicStats } from '../models/entity-model-customization.model';
import { CimSubstationPopupDto } from '../models/cim-substation-map.model';

@Injectable({
  providedIn: 'root',
})
export class TaggedSubstationService {
  private pathApi: string;

  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  constructor(
    private http: HttpClient,
    @Inject(SHARED_LIB_CONFIG) private config: SharedLibConfig,
  ) {
    this.pathApi = this.config.pathApi + 'cim/TaggedSubstation/';
  }

  getByGuid(guid: string, utilityId: number): Observable<any> {
    return this.http.post<ResponsePackage<any>>(
      `${this.pathApi}getByGuid?guid=${guid}&utilityId=${utilityId}`,
      { headers: this.headers, observe: 'response' },
    );
  }

  reviewSubstation(substationId: number): Observable<any> {
    return this.http.post(`${this.pathApi}review?substationId=${substationId}`, null, {
      headers: this.headers,
      observe: 'response',
    });
  }

  assignSubstation(dataIn: any): Observable<any> {
    return this.http.post<ResponsePackage<string>>(
      `${this.pathApi}assign`,
      dataIn,
      { observe: 'response' },
    );
  }

  audit(id: number): Observable<ResponsePackage<any>> {
    return this.http.post<ResponsePackage<any>>(
      `${this.pathApi}audit/${id}`,
      null,
    );
  }

  getAll(originalFilter: Filter): Observable<any> {
    const dataIn = {
      ...originalFilter,
      filterParams: {
        ...originalFilter.filterParams,
        selectedAreas: originalFilter.filterParams.selectedAreaModels?.map((a) => a.id) || [],
        selectedUsers: originalFilter.filterParams.selectedUserModels?.map((u) => u.id) || [],
        selectedTransmissionStations: originalFilter.filterParams.selectedTsModels?.map((ts) => ts.id) || [],
      },
    };
    return this.http.post(`${this.pathApi}getAll`, dataIn, {
      observe: 'response',
    });
  }

  getStatsDynamic(obj: DynamicStats): Observable<any> {
    return this.http.post(`${this.pathApi}getStatsDynamic`, obj, {
      headers: this.headers,
      observe: 'response',
    });
  }

  downloadDynamic(originalFilter: Filter): Observable<any> {
    const dataIn = {
      ...originalFilter,
      pageInfo: new PageInfo(-1, -1),
      filterParams: {
        ...originalFilter.filterParams,
        selectedAreas: originalFilter.filterParams.selectedAreaModels?.map((a) => a.id) || [],
        selectedUsers: originalFilter.filterParams.selectedUserModels?.map((u) => u.id) || [],
        selectedTransmissionStations: originalFilter.filterParams.selectedTsModels?.map((ts) => ts.id) || [],
      },
    };

    return this.http.post(`${this.pathApi}downloadFileDynamic`, dataIn);
  }

  getEquipmentInstancesForAsset(
    assetGuid: string,
    substationTemplateId?: number | null,
    equipmentType?: number | null,
  ) {
    const body = { assetGuid, substationTemplateId, equipmentType };
    return this.http.post<ResponsePackage<any>>(
      `${this.pathApi}getEquipmentInstancesForAsset`,
      body,
      { headers: this.headers, observe: 'response' },
    );
  }

  updateAttributeValues(dataIn: {
    substationId: number;
    attributeValues: {
      attributeDefinitionId: number;
      doubleValue?: number | null;
      stringValue?: string | null;
      dateValue?: string | null;
      urlValue?: string | null;
      selectedOptionIds?: number[];
    }[];
  }) {
    return this.http.post<ResponsePackage<any>>(
      `${this.pathApi}updateAttributeValues`,
      dataIn,
      { headers: this.headers, observe: 'response' },
    );
  }

  getSubstationDataQuality(id: number): Observable<any> {
    return this.http.get(`${this.pathApi}getSubstationDataQuality/${id}`, {
      observe: 'response',
    });
  }

  rejectSubstationTemplate(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}rejectSubstation`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  approveSubstationTemplate(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}approveSubstation`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  recall(dataIn: any): Observable<any> {
    return this.http.post(`${this.pathApi}recall`, dataIn, {
      headers: this.headers,
      observe: 'response',
    });
  }

  getEquipmentForAsset(
    assetGuid: string,
    substationTemplateId?: number | null,
  ) {
    const body = { assetGuid, substationTemplateId: substationTemplateId ?? 0 };
    return this.http.post<ResponsePackage<any>>(
      `${this.pathApi}getEquipmentForAsset`,
      body,
      { headers: this.headers, observe: 'response' },
    );
  }

  getMapDetails(id: number) {
    return this.http.get<ResponsePackage<any>>(
      `${this.pathApi}getMapDetails/${id}`,
      { headers: this.headers, observe: 'response' },
    );
  }

  getPopupInfo(id: number) {
    return this.http.get<ResponsePackage<CimSubstationPopupDto>>(
      `${this.pathApi}getPopupInfo/${id}`,
      { headers: this.headers, observe: 'response' },
    );
  }

  getSchematicFromAdvancedMap(substationId: number): Observable<any> {
    return this.http.get(`${this.pathApi}getSchema/${substationId}`, {
      observe: 'response',
    });
  }
}
